import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, MapPin, Grid3X3, List, Loader2, Navigation } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SearchBar } from "@/components/SearchBar";
import { BusinessCard } from "@/components/BusinessCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { businessesApi, categoriesApi, departmentsApi } from "@/lib/api";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);

  // Filters from URL
  const query = searchParams.get("q") || "";
  const city = searchParams.get("city") || "";
  const categoryId = searchParams.get("category_id") || "";
  const departmentCode = searchParams.get("department_code") || "";

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [catRes, deptRes] = await Promise.all([
          categoriesApi.getAll(),
          departmentsApi.getAll(),
        ]);
        setCategories(catRes.data);
        setDepartments(deptRes.data);
      } catch (error) {
        console.error("Error loading filters:", error);
      }
    };
    loadFilters();
  }, []);

  useEffect(() => {
    const search = async () => {
      setLoading(true);
      try {
        const params = {
          query: query || undefined,
          city: city || undefined,
          category_id: categoryId || undefined,
          department_code: departmentCode || undefined,
          page: currentPage,
          limit: 20,
        };

        if (userLocation) {
          params.latitude = userLocation.latitude;
          params.longitude = userLocation.longitude;
          params.radius_km = 50;
        }

        const res = await businessesApi.search(params);
        setBusinesses(res.data.businesses);
        setTotalResults(res.data.total);
        setTotalPages(res.data.total_pages);
      } catch (error) {
        console.error("Error searching:", error);
      } finally {
        setLoading(false);
      }
    };

    search();
  }, [query, city, categoryId, departmentCode, currentPage, userLocation]);

  const handleFilterChange = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      alert("La géolocalisation n'est pas supportée par votre navigateur.");
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setGeoLoading(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        alert("Impossible d'obtenir votre position.");
        setGeoLoading(false);
      }
    );
  };

  const clearGeolocation = () => {
    setUserLocation(null);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="search-page">
      <Header />

      {/* Search Header */}
      <div className="bg-white border-b border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SearchBar initialQuery={query} initialCity={city} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Filters Sidebar */}
          <aside className={`lg:col-span-3 ${showFilters ? "block" : "hidden lg:block"}`}>
            <div className="bg-white rounded-lg border border-slate-200 p-6 sticky top-24">
              <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filtres
              </h3>

              {/* Geolocation */}
              <div className="mb-6">
                <label className="form-label">Géolocalisation</label>
                {userLocation ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <Navigation className="w-4 h-4" />
                      Position active
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearGeolocation}
                      className="text-red-500 hover:text-red-700"
                    >
                      Désactiver
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleGeolocation}
                    disabled={geoLoading}
                    data-testid="geolocation-btn"
                  >
                    {geoLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Navigation className="w-4 h-4 mr-2" />
                    )}
                    Utiliser ma position
                  </Button>
                )}
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <label className="form-label">Catégorie</label>
                <Select
                  value={categoryId || "all"}
                  onValueChange={(value) => handleFilterChange("category_id", value === "all" ? "" : value)}
                >
                  <SelectTrigger className="w-full" data-testid="category-filter">
                    <SelectValue placeholder="Toutes les catégories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les catégories</SelectItem>
                    {categories.filter(cat => cat.id).map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Department Filter */}
              <div className="mb-6">
                <label className="form-label">Département</label>
                <Select
                  value={departmentCode || "all"}
                  onValueChange={(value) => handleFilterChange("department_code", value === "all" ? "" : value)}
                >
                  <SelectTrigger className="w-full" data-testid="department-filter">
                    <SelectValue placeholder="Tous les départements" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les départements</SelectItem>
                    {departments.filter(dept => dept.code).map((dept) => (
                      <SelectItem key={dept.code} value={dept.code}>
                        {dept.code} - {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSearchParams({});
                  setUserLocation(null);
                }}
                data-testid="clear-filters-btn"
              >
                Effacer les filtres
              </Button>
            </div>
          </aside>

          {/* Results */}
          <main className="lg:col-span-9">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {totalResults} résultat{totalResults !== 1 ? "s" : ""}
                </h2>
                {(query || city) && (
                  <p className="text-sm text-slate-600">
                    {query && `"${query}"`} {city && `à ${city}`}
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                className="lg:hidden"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtres
              </Button>
            </div>

            {/* Results List */}
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="flex gap-4">
                      <div className="skeleton w-20 h-20 rounded-lg"></div>
                      <div className="flex-1 space-y-3">
                        <div className="skeleton h-5 w-3/4"></div>
                        <div className="skeleton h-4 w-1/4"></div>
                        <div className="skeleton h-4 w-full"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : businesses.length === 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
                <Grid3X3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Aucun résultat trouvé
                </h3>
                <p className="text-slate-600 mb-4">
                  Essayez de modifier vos critères de recherche ou vos filtres.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSearchParams({})}
                >
                  Effacer les filtres
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {businesses.map((business) => {
                  const category = categories.find(c => c.id === business.category_id);
                  return (
                    <BusinessCard 
                      key={business.id} 
                      business={business} 
                      category={category}
                    />
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Précédent
                </Button>
                <span className="px-4 text-sm text-slate-600">
                  Page {currentPage} sur {totalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Suivant
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
